import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function IncentiveSlabs() {
  const [tier, setTier] = useState("assistant");
  const [tab, setTab] = useState("assistant");
  const [cfg, setCfg] = useState(null);
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/targets").then(r => setTargets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCfg(null);
    setTab(tier);
    api.get(`/slabs/${tier}`).then(r => {
      if (!cancelled) { setCfg(r.data.config); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [tier]);

  const saveTargets = async () => {
    try {
      await api.put("/targets", targets);
      toast.success("Targets saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const save = async () => {
    try {
      await api.put(`/slabs/${tier}`, { config: cfg });
      toast.success("Slabs updated");
    } catch (e) { toast.error("Save failed"); }
  };

  const updateRevSlab = (i, field, val) => {
    setCfg(c => {
      const slabs = [...c.revenue_slabs];
      slabs[i] = { ...slabs[i], [field]: field === "rate_per_lakh" ? Number(val) : val === "" ? null : Number(val) };
      return { ...c, revenue_slabs: slabs };
    });
  };

  const updateBonusAmount = (group, key, val) => {
    setCfg(c => {
      if (tier === "assistant") {
        const arr = c.bonuses[group].map(b => b.key === key ? { ...b, amount: Number(val) } : b);
        return { ...c, bonuses: { ...c.bonuses, [group]: arr } };
      } else {
        const arr = c[group].map(b => b.key === key ? { ...b, amount: Number(val) } : b);
        return { ...c, [group]: arr };
      }
    });
  };

  const updateSalaryRev = (i, field, val) => {
    setCfg(c => {
      const arr = [...(c.salary_revision||[])];
      arr[i] = { ...arr[i], [field]: field === "label" ? val : Number(val) };
      return { ...c, salary_revision: arr };
    });
  };

  const isAssistant = tier === "assistant";
  if (tab !== "targets" && !cfg) return <div className="text-muted-foreground" data-testid="slabs-root">Loading…</div>;
  const bonuses = isAssistant && cfg ? cfg.bonuses : null;

  return (
    <div className="space-y-6" data-testid="slabs-root">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Settings</div>
          <h1 className="mt-2 text-3xl font-heading font-medium">Incentive & Doctor Targets</h1>
          <div className="gold-underline mt-3" />
        </div>
        {tab !== "targets" && <Button onClick={save} className="gold-bg hover:opacity-90" data-testid="slabs-save-btn">Save Configuration</Button>}
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v !== "targets") setTier(v); }}>
        <TabsList className="bg-navy-800 border border-navy-700">
          <TabsTrigger value="assistant" data-testid="tab-assistant">Assistant Doctor</TabsTrigger>
          <TabsTrigger value="branch_head" data-testid="tab-branch_head">Branch Head</TabsTrigger>
          <TabsTrigger value="targets" data-testid="tab-targets">Doctor Targets</TabsTrigger>
        </TabsList>

        {tab === "targets" ? (
          <TabsContent value="targets" className="mt-4">
            <Card className="bg-navy-800 border-navy-700">
              <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-lg">Monthly Educational Contribution Targets</CardTitle></CardHeader>
              <CardContent className="p-5 space-y-4">
                {[
                  ["ppt_target", "Monthly PPT Target"],
                  ["shoots_target", "Monthly Educational Shoot Target"],
                  ["videos_target", "Monthly Informative Video Target"],
                  ["teaching_target", "Monthly Teaching / Student Session Target"],
                ].map(([k, label]) => (
                  <div key={k} className="grid grid-cols-2 gap-4 items-center">
                    <div>
                      <div className="text-sm">{label}</div>
                      <div className="text-xs text-muted-foreground">Configurable monthly target per doctor.</div>
                    </div>
                    <Input type="number" value={targets?.[k] ?? ""} onChange={e => setTargets(t => ({ ...t, [k]: Number(e.target.value) }))} className="bg-navy-900 border-navy-700 num" data-testid={`target-${k}`} />
                  </div>
                ))}
                <Button className="gold-bg hover:opacity-90" onClick={saveTargets} data-testid="save-targets-btn">Save Targets</Button>
              </CardContent>
            </Card>
          </TabsContent>
        ) : (
          <TabsContent value={tab} className="space-y-6 mt-4">
          <Card className="bg-navy-800 border-navy-700">
            <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-lg">Revenue Slabs (₹/lakh)</CardTitle></CardHeader>
            <CardContent className="p-5 space-y-3">
              {cfg.revenue_slabs.map((s, i) => (
                <div key={i} className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs">Min (₹)</Label><Input type="number" value={s.min ?? ""} onChange={e => updateRevSlab(i, "min", e.target.value)} className="bg-navy-900 border-navy-700 num" /></div>
                  <div><Label className="text-xs">Max (₹) — blank = ∞</Label><Input type="number" value={s.max ?? ""} onChange={e => updateRevSlab(i, "max", e.target.value)} className="bg-navy-900 border-navy-700 num" /></div>
                  <div><Label className="text-xs">Rate per Lakh (₹)</Label><Input type="number" value={s.rate_per_lakh} onChange={e => updateRevSlab(i, "rate_per_lakh", e.target.value)} className="bg-navy-900 border-navy-700 num" /></div>
                </div>
              ))}
            </CardContent>
          </Card>

          {isAssistant && bonuses && Object.entries(bonuses).map(([grp, arr]) => (
            <Card key={grp} className="bg-navy-800 border-navy-700">
              <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base capitalize">{grp.replaceAll("_"," ")} Bonuses</CardTitle></CardHeader>
              <CardContent className="p-5 space-y-3">
                {arr.map(b => (
                  <div key={b.key} className="grid grid-cols-3 gap-3 items-center">
                    <div className="col-span-2 text-sm">{b.label}</div>
                    <Input type="number" value={b.amount} onChange={e => updateBonusAmount(grp, b.key, e.target.value)} className="bg-navy-900 border-navy-700 num" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          {!isAssistant && ["milestones","conversion_bonus","end_to_end","business_expansion","satisfaction","operational"].map(grp => (
            <Card key={grp} className="bg-navy-800 border-navy-700">
              <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base capitalize">{grp.replaceAll("_"," ")}</CardTitle></CardHeader>
              <CardContent className="p-5 space-y-3">
                {(cfg[grp]||[]).map(b => (
                  <div key={b.key} className="grid grid-cols-3 gap-3 items-center">
                    <div className="col-span-2 text-sm">{b.label}</div>
                    <Input type="number" value={b.amount} onChange={e => updateBonusAmount(grp, b.key, e.target.value)} className="bg-navy-900 border-navy-700 num" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          {!isAssistant && (
            <Card className="bg-navy-800 border-navy-700">
              <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Salary Revision Milestones</CardTitle></CardHeader>
              <CardContent className="p-5 space-y-3">
                {(cfg.salary_revision||[]).map((m, i) => (
                  <div key={i} className="grid grid-cols-4 gap-3">
                    <div><Label className="text-xs">Threshold (₹)</Label><Input type="number" value={m.threshold} onChange={e => updateSalaryRev(i, "threshold", e.target.value)} className="bg-navy-900 border-navy-700 num" /></div>
                    <div><Label className="text-xs">Months</Label><Input type="number" value={m.months} onChange={e => updateSalaryRev(i, "months", e.target.value)} className="bg-navy-900 border-navy-700 num" /></div>
                    <div><Label className="text-xs">New Salary (₹)</Label><Input type="number" value={m.new_salary} onChange={e => updateSalaryRev(i, "new_salary", e.target.value)} className="bg-navy-900 border-navy-700 num" /></div>
                    <div><Label className="text-xs">Label</Label><Input value={m.label||""} onChange={e => updateSalaryRev(i, "label", e.target.value)} className="bg-navy-900 border-navy-700" /></div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
